import React, { useState, useEffect } from "react";
import { StyleSheet, View, Platform, TouchableOpacity } from "react-native";
import { YAxis, BarChart } from "react-native-svg-charts";
import { Line, Circle, Text, G, ForeignObject } from "react-native-svg";

import moment from "moment";
import { findIndex } from "lodash";

import Utils, { sortBy } from "../../../../../helpers/utils";
import AppTheme from "../../../AppTheme";
import { getMoodById } from "../../../common/mood/MoodsHelper";

import FORMATS from "../../../reflection/header/formats";
const DATE_FORMAT = "YYYY-MM-DD";

export default function ComparisonChart({
  data,
  progressColor,
  selectedDate,
  timeUnit,
  selectedAverages = {},
  onDateChanged,
  showJournalDialog = () => {},
  showToggleChartMenu,
}) {
  const [barData, setBarData] = useState([]);
  const [hmax, setHmax] = useState(0);

  useEffect(() => {
    const MAX_COUNT = 6;
    let h_min = -1;
    let h_max = 0;
    const bd = [];

    const selectedDay = moment(selectedDate).format(DATE_FORMAT);
    const reflectionHours = data.reflection_hours;
    if (!reflectionHours) return;

    const allDays = sortBy(Object.keys(reflectionHours));

    // Current selected gem
    let selectedDayIndex = findIndex(allDays, (d) => d == selectedDate);
    if (selectedDayIndex == -1) selectedDayIndex = 0;

    const currentPage = Math.floor(selectedDayIndex / MAX_COUNT) + 1;

    const end = Math.min(allDays.length, currentPage * MAX_COUNT);
    const start = Math.max(0, end - MAX_COUNT);
    const chartDays = sortBy(allDays.slice(start, end));

    // Build chart data
    const dateFormat = FORMATS[timeUnit].format || "M/D";
    chartDays.forEach((key, index) => {
      const d = reflectionHours[key];
      const day = key;

      const label = Utils.formatDateByTimeUnit({
        date:
          timeUnit == "week"
            ? moment(day).endOf("isoweek").format("YYYY-MM-DD")
            : day,
        timeUnit,
        format: dateFormat,
      });

      if (d.hours != null) {
        // Hours
        let h = adjustProjection(1 * d.hours);
        if (h > h_max) h_max = h;
        if (h_min < 0 || h < h_min) h_min = h;

        // Moods
        let bar = { gem: { day: key } }; // for journal dialog
        const isCurrentDay = selectedDay == day;
        let svgFillColor = isCurrentDay ? progressColor : "#DADADA";

        const gem = d.gem;
        if (gem?.mood) {
          const mood = getMoodById(gem.mood);
          bar = {
            gem: {
              ...d.gem,
              color: mood.color,
              textColor: mood.textColor,
            },
          };
        }

        bd.push({
          key: `bar-${index}`,
          value: h,
          date: key,
          label,
          isCurrentDay, // for current day display gem line indicator
          svg: { fill: svgFillColor },
          ...bar,
        });
      }
    });

    setBarData([...bd]);
    setHmax(h_max);
  }, [data, selectedDate]);

  const adjustProjection = (h) => {
    try {
      if (hours.length == 0 && chartDays.length > 1) {
        const data1 = reflectionHours[chartDays[0]];
        const data2 = reflectionHours[chartDays[1]];
        const interval = moment(data1.date).diff(moment(data2.date), "hours");
        const now = Utils.nowUserTz(controller);
        const end = moment(data1.date).add(interval, "hours");
        if (end.isAfter(now)) {
          const delta = moment(end).diff(now, "hours");
          if (delta > 0) {
            let sampleHours = 0;
            let sampleDays = 0;
            for (var i = 1; i <= Math.min(5, chartDays.length); i++) {
              const d = reflectionHours[chartDays[i]];
              sampleHours += d.hours;
              sampleDays += 1;
            }
            const projection =
              (1.0 * delta * sampleHours) / (interval * sampleDays);
            return h + projection;
          }
        }
      }
    } catch (e) {
      //ignore
    }
    return h;
  };

  const LineDecorator = ({ y, hours }) => {
    if (!hours) return null;

    const lineWidth = "100%";

    // Gem
    return hours.isGem ? (
      <Line
        x1={3}
        x2={lineWidth}
        y1={y(hours.value)}
        y2={y(hours.value)}
        stroke={hours.color}
        strokeWidth="2"
        strokeLinecap="round"
      />
    ) : (
      // Lifetime / Atus
      <Line
        x1={3}
        x2={lineWidth}
        y1={y(hours.value)}
        y2={y(hours.value)}
        stroke={hours.color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="3, 6"
      />
    );
  };

  const XAxisGems = ({ x, hours }) => {
    const tileSize = 24;
    const count = hours.length;
    const offset = count == 3 ? 30 : 15;

    return (
      <G
        style={{
          height: tileSize,
          zIndex: 1000,
          transform: [{ translateY: 96 }],
        }}
      >
        {hours.map((h, index) => (
          <G
            key={index}
            x={x(index) + offset}
            y={0}
            width={tileSize}
            height={tileSize}
          >
            {h.gem?.mood ? (
              <Circle
                r={tileSize / 2}
                stroke={h.gem.color}
                strokeWidth={6}
                fill={h.gem.color}
              />
            ) : null}
            <Text
              textAnchor="middle"
              alignmentBaseline="middle"
              style={[styles.label, h.gem && { fill: h.gem.textColor }]}
            >
              {h.label}
            </Text>

            {onDateChanged ? (
              <ForeignObject
                width={tileSize}
                height={tileSize}
                x={-tileSize / 2}
                y={-tileSize / 2}
              >
                <TouchableOpacity
                  onPress={() => onDateChanged(h)}
                  onLongPress={() => showJournalDialog(h.gem)}
                  style={{
                    width: tileSize,
                    height: tileSize,
                    borderRadius: tileSize / 2,
                  }}
                />
              </ForeignObject>
            ) : null}
          </G>
        ))}
      </G>
    );
  };

  // -----------------------------------------------------------------------------------
  let recentAverage = data.averages ? data.averages.recent : null;
  let atusAverage = data.averages ? data.averages.atus : null;

  const axesSvg = {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 9,
    lineHeight: 11,
    letterSpacing: 0.175,
    fill: AppTheme.almostBlack,
  };

  const hours = barData.map((b) => b.value);

  const goalTargetHours = data.goal?.goal_hours
    ? data.goal.goal_hours[timeUnit]?.partial_target_hours
    : null;

  return (
    <View style={[styles.chartContainer]}>
      <TouchableOpacity
        style={{ flex: 1 }}
        onLongPress={showToggleChartMenu ? showToggleChartMenu : null}
      >
        <BarChart
          style={styles.barChart}
          data={barData}
          yAccessor={({ item }) => item.value}
          gridMin={0}
          gridMax={hmax}
          spacingInner={0.3}
          svg={{
            fill: "#DADADA",
            strokeLinecap: "round",
            strokeLinejoin: "round",
          }}
          horizontal={false}
          contentInset={{ bottom: 40, top: 2, left: 2, right: 2 }}
        >
          {goalTargetHours ? (
            <LineDecorator
              hours={{
                value: goalTargetHours,
                color: progressColor,
                isGem: true,
              }}
            />
          ) : null}

          {/* Personal average */}
          {!selectedAverages["lifetime"] && recentAverage ? (
            <LineDecorator
              hours={{
                value: recentAverage,
                color: AppTheme.DEFAULT_COLOR,
              }}
            />
          ) : null}

          {/* ATUS average */}
          {!selectedAverages["atus"] && atusAverage ? (
            <LineDecorator
              hours={{ value: atusAverage, color: AppTheme.atusColor }}
            />
          ) : null}

          <XAxisGems hours={barData} />
          {/* <Grid/> */}
        </BarChart>
      </TouchableOpacity>

      <YAxis
        style={styles.yAxis}
        data={hours}
        contentInset={{ bottom: 40, top: 4, left: 2, right: 2 }}
        numberOfTicks={4}
        svg={axesSvg}
        min={0}
        max={hmax}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  chartContainer: {
    flexDirection: "row",
    flex: 1,
    paddingTop: 5,
    height: 120,
  },
  barChart: {
    height: 120,
  },
  yAxis: {
    height: 120,
    width: 20,
  },
  label: Platform.select({
    ios: {
      fontFamily: "Montserrat",
      fontWeight: "600",
      fontSize: 9,
      lineHeight: 11,
      letterSpacing: 0.175,
    },
    android: {
      fontFamily: "Montserrat-SemiBold",
      fontWeight: "600",
      fontSize: 9,
      lineHeight: 11,
      letterSpacing: 0.175,
    },
    web: {
      fontFamily: "Montserrat",
      fontWeight: "600",
      fontSize: 9,
      lineHeight: 11,
      fill: AppTheme.almostBlack,
    },
  }),
});
